import { supabase } from "@/integrations/supabase/client";

export type Segment = {
  slug: string;
  kind: "make" | "fuel";
  value: string;
  name: string;
  /** Old auto-written article slug under /blog/stiri, redirected here. */
  legacySlug?: string;
};

export const SEGMENTS: Segment[] = [
  { slug: "bmw", kind: "make", value: "BMW", name: "BMW", legacySlug: "statistici-bmw-romania" },
  { slug: "mercedes-benz", kind: "make", value: "Mercedes-Benz", name: "Mercedes-Benz", legacySlug: "statistici-mercedes-romania" },
  { slug: "volkswagen", kind: "make", value: "Volkswagen", name: "Volkswagen", legacySlug: "statistici-volkswagen-romania" },
  { slug: "audi", kind: "make", value: "Audi", name: "Audi", legacySlug: "statistici-audi-romania" },
  { slug: "skoda", kind: "make", value: "Skoda", name: "Škoda", legacySlug: "statistici-skoda-romania" },
  { slug: "dacia", kind: "make", value: "Dacia", name: "Dacia", legacySlug: "statistici-dacia-romania" },
  { slug: "ford", kind: "make", value: "Ford", name: "Ford", legacySlug: "statistici-ford-romania" },
  { slug: "renault", kind: "make", value: "Renault", name: "Renault", legacySlug: "statistici-renault-romania" },
  { slug: "opel", kind: "make", value: "Opel", name: "Opel", legacySlug: "statistici-opel-romania" },
  { slug: "toyota", kind: "make", value: "Toyota", name: "Toyota", legacySlug: "statistici-toyota-romania" },
  { slug: "mg", kind: "make", value: "MG", name: "MG" },
  { slug: "peugeot", kind: "make", value: "Peugeot", name: "Peugeot" },
  { slug: "kia", kind: "make", value: "Kia", name: "Kia" },
  { slug: "diesel", kind: "fuel", value: "diesel", name: "Diesel", legacySlug: "masini-diesel-romania-statistici" },
  { slug: "hibrid", kind: "fuel", value: "hybrid", name: "Hibrid", legacySlug: "masini-hibrid-romania-statistici" },
  { slug: "electrice", kind: "fuel", value: "electric", name: "Electrice", legacySlug: "masini-electrice-romania-statistici" },
];

/** Below this many active listings a page is shown but not indexed. */
export const MIN_INDEXABLE = 30;

export const segmentBySlug = (s: string) => SEGMENTS.find((x) => x.slug === s);
export const segmentByLegacySlug = (s: string) => SEGMENTS.find((x) => x.legacySlug === s);

export const segmentTitle = (seg: Segment) =>
  seg.kind === "make"
    ? `Prețuri ${seg.name} second hand în România`
    : `Prețuri mașini ${seg.name.toLowerCase()} second hand în România`;

export const segmentSearchHref = (seg: Segment) =>
  seg.kind === "make" ? `/vehicles?make=${encodeURIComponent(seg.value)}` : `/vehicles?fuelType=${seg.value}`;

const FUEL_LABEL: Record<string, string> = {
  diesel: "Diesel", benzina: "Benzină", hybrid: "Hibrid", hybrid_plugin: "Hibrid plug-in", electric: "Electric", lpg: "GPL",
};
export const fuelLabel = (f: string) => FUEL_LABEL[f] ?? f;

type Row = {
  price: number | null; currency: string | null; year: number | null; mileage_value: number | null;
  mileage_unit: string | null; make: string | null; model: string | null; location_city: string | null;
  fuel_type: string | null; status: string; created_at: string;
};

const median = (a: number[]) => {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
};
const quantile = (a: number[], q: number) => {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  const pos = (s.length - 1) * q;
  const lo = Math.floor(pos), hi = Math.ceil(pos);
  return Math.round(s[lo] + (s[hi] - s[lo]) * (pos - lo));
};
/** Same rule as DB valid_mileage_km: miles converted, valid 0..1,500,000 km. */
const validKm = (v: number | null, unit: string | null) => {
  if (v == null || !Number.isFinite(Number(v))) return null;
  const km = /mi/i.test(unit ?? "") ? Number(v) * 1.60934 : Number(v);
  return km > 0 && km <= 1_500_000 ? Math.round(km) : null;
};
const validPrice = (r: Row) =>
  r.price && r.price > 0 && (!r.currency || r.currency.toUpperCase() === "EUR") ? Number(r.price) : null;

export type SegmentStats = Awaited<ReturnType<typeof fetchSegmentStats>>;

export async function fetchSegmentStats(seg: Segment) {
  let q = supabase
    .from("listings_v2")
    .select("price, currency, year, mileage_value, mileage_unit, make, model, location_city, fuel_type, status, created_at")
    .eq("country_code", "RO")
    .in("status", ["active", "sold"])
    .limit(3000);
  q = seg.kind === "make" ? q.eq("make", seg.value) : q.eq("fuel_type", seg.value);
  const { data, error } = await q;
  if (error) throw error;
  const rows = (data ?? []) as Row[];
  const active = rows.filter((r) => r.status === "active");

  const prices = active.map(validPrice).filter((p): p is number => p != null);
  const years = active.map((r) => Number(r.year)).filter((y) => y > 1950 && y <= new Date().getFullYear() + 1);
  const kms = active.map((r) => validKm(r.mileage_value, r.mileage_unit)).filter((k): k is number => k != null);

  const group = (key: (r: Row) => string | null) => {
    const m = new Map<string, number[]>();
    for (const r of active) {
      const k = key(r);
      if (!k) continue;
      const p = validPrice(r);
      if (!m.has(k)) m.set(k, []);
      if (p != null) m.get(k)!.push(p);
    }
    const counts = new Map<string, number>();
    for (const r of active) { const k = key(r); if (k) counts.set(k, (counts.get(k) ?? 0) + 1); }
    return [...counts.entries()]
      .map(([label, count]) => ({ label, count, medianPrice: median(m.get(label) ?? []) }))
      .sort((a, b) => b.count - a.count);
  };

  const models = group((r) =>
    seg.kind === "make" ? (r.model?.trim() || null) : [r.make, r.model].filter(Boolean).join(" ").trim() || null,
  ).slice(0, 10);
  const cities = group((r) => r.location_city?.trim() || null).slice(0, 8);
  const fuels = seg.kind === "make" ? group((r) => r.fuel_type || null) : [];

  // Monthly median asking price of listings published in that month (active or sold today).
  const months: { month: string; count: number; medianPrice: number | null }[] = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const key = d.toISOString().slice(0, 7);
    const ps = rows.filter((r) => r.created_at?.startsWith(key)).map(validPrice).filter((p): p is number => p != null);
    months.push({ month: key, count: ps.length, medianPrice: ps.length >= 5 ? median(ps) : null });
  }

  return {
    total: active.length,
    priced: prices.length,
    p25: quantile(prices, 0.25),
    medianPrice: median(prices),
    p75: quantile(prices, 0.75),
    minPrice: prices.length ? Math.min(...prices) : null,
    maxPrice: prices.length ? Math.max(...prices) : null,
    medianYear: median(years),
    medianKm: median(kms),
    kmCount: kms.length,
    models, cities, fuels, months,
    soldCount: rows.length - active.length,
    generatedAt: new Date().toISOString(),
  };
}
